import React, { memo, useCallback, useEffect, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useParams, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { canRescheduleJob } from 'src/components/scheduler/detail/canRescheduleJob'
import { RescheduleJobPanel } from 'src/components/scheduler/detail/RescheduleJobPanel'
import { RetriesHistoryTable } from 'src/components/scheduler/detail/RetriesHistoryTable'
import { BackButton } from 'src/components/shared/BackButton'
import { ContentCard } from 'src/components/shared/ContentCard'
import { SectionCard } from 'src/components/shared/SectionCard'
import { AdminFetchRetryConfig } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { buildScheduledActionPath } from 'src/constants/routes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { RetryActionJob } from 'src/interfaces/retryConfig'
import {
  getRetryConfigDetail,
  getRetryConfigDetailError,
  getRetryConfigDetailLoading,
} from 'src/selectors/retryConfig'

/**
 * An action's retry jobs and the reschedule form, as their own page under the detail screen.
 *
 * The reschedule panel lives here rather than on the detail page because it is opened from a row
 * of the table above it — split across two screens, pressing Reschedule would navigate away from
 * the form it opens.
 *
 * Reads the same `retryConfig` detail slice as the detail page and issues the same fetch, so a
 * deep link straight to this URL loads on its own rather than depending on the detail page having
 * run first.
 */
const ScheduledActionRetriesPageComponent = () => {
  const { configId } = useParams<{ configId: string }>()
  const [searchParams] = useSearchParams()

  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const detail = useSelector(getRetryConfigDetail)
  const isLoading = useSelector(getRetryConfigDetailLoading)
  const detailError = useSelector(getRetryConfigDetailError)

  const [selectedJob, setSelectedJob] = useState<RetryActionJob | null>(null)

  // PUT …/retry-jobs/:retryJobId. Without it the history is read-only: no Reschedule column and
  // no panel (ENG-1207 part 2).
  const canManageSchedulers = useHasPermission(InternalPermission.SchedulersManage)

  useEffect(() => {
    if (!isAuthenticated || !configId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchRetryConfig.request({ configId }))
  }, [asyncDispatchWithErrorToast, configId, isAuthenticated])

  // A stale panel must not be left open against a job that has since run: every time the detail
  // refetches (initial load, a reschedule), drop the selection if the selected job is gone from
  // the refreshed list or is no longer reschedulable.
  useEffect(() => {
    if (!selectedJob) {
      return
    }

    const refreshedJob = detail.actionJobs.find((job) => {
      return job.id === selectedJob.id
    })

    if (!refreshedJob || !canRescheduleJob(refreshedJob)) {
      setSelectedJob(null)
    }
  }, [detail.actionJobs, selectedJob])

  const handleCancelReschedule = useCallback(() => {
    setSelectedJob(null)
  }, [])

  // Carries the query string so going back up to the detail page — and from there to the list —
  // still lands on the partner and page the user started from.
  const detailPath = {
    pathname: configId ? buildScheduledActionPath(configId) : '',
    search: searchParams.toString(),
  }

  return (
    <div
      data-testid="scheduled-action-retries-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-y-auto p-6`}
    >
      <ContentCard className={tw`flex flex-col gap-4`}>
        <BackButton
          label="Back to scheduled action"
          to={detailPath}
          dataTestId="scheduled-action-retries-back-button"
        />
        <Typography variant="h2">Retries history</Typography>
      </ContentCard>

      {detailError ? (
        <SectionCard title="Retries history">
          <Typography variant="body2" css={tw`text-critical-500`}>
            {detailError}
          </Typography>
        </SectionCard>
      ) : null}

      <SectionCard title="Retries history">
        <RetriesHistoryTable
          actionJobs={detail.actionJobs}
          isLoading={isLoading}
          onReschedule={canManageSchedulers ? setSelectedJob : null}
        />
      </SectionCard>

      {configId && canManageSchedulers ? (
        <RescheduleJobPanel
          configId={configId}
          selectedJob={selectedJob}
          onCancel={handleCancelReschedule}
        />
      ) : null}
    </div>
  )
}

export const ScheduledActionRetriesPage = memo(ScheduledActionRetriesPageComponent)
